/**
 * Biome scaffolder: creates biome.json if no existing linter is detected
 */

import fs from 'node:fs'
import path from 'node:path'

import type { LinterInfo } from '@next-adapter/config/detect.js'

export interface BiomeScaffoldResult {
  /** Whether Biome was set up */
  installed: boolean
  /** Reason it was skipped (if any) */
  skippedReason: string | null
}

/**
 * Create biome.json config if no linter exists.
 * Does NOT install the package — the dependency installer (task 5.05) handles that.
 */
export function scaffoldBiome(cwd: string, linter: LinterInfo): BiomeScaffoldResult {
  if (linter.type !== 'none') {
    return {
      installed: false,
      skippedReason: `${linter.type} already configured (${linter.configFile})`
    }
  }

  const configPath = path.join(cwd, 'biome.json')
  if (fs.existsSync(configPath)) {
    return { installed: false, skippedReason: 'biome.json already exists' }
  }

  const config = {
    $schema: 'https://biomejs.dev/schemas/2.3.10/schema.json',
    vcs: {
      enabled: true,
      clientKind: 'git',
      useIgnoreFile: true
    },
    assist: {
      actions: {
        source: {
          organizeImports: 'on'
        }
      }
    },
    formatter: {
      enabled: true,
      indentStyle: 'space',
      indentWidth: 2,
      lineWidth: 100
    },
    linter: {
      enabled: true,
      rules: {
        recommended: true,
        correctness: {
          noUnusedImports: 'warn',
          noUnusedVariables: 'warn'
        },
        style: {
          noNonNullAssertion: 'off'
        }
      }
    },
    javascript: {
      formatter: {
        quoteStyle: 'single',
        trailingCommas: 'all',
        semicolons: 'asNeeded'
      }
    },
    files: {
      includes: [
        '**',
        '!!**/node_modules',
        '!!**/.next',
        '!!**/dist',
        '!!**/build',
        '!!**/out',
        '!!**/coverage',
        '!!**/.turbo',
        '!!**/.vercel',
        '!!**/.betterstart/snapshots/**/files',
        '!!**/*.tsbuildinfo',
        '!!**/next-env.d.ts',
        '!**/*.config.js',
        '!**/*.config.mjs',
        '!**/*.config.cjs'
      ]
    }
  }

  fs.writeFileSync(configPath, `${JSON.stringify(config, null, 2)}\n`, 'utf-8')
  return { installed: true, skippedReason: null }
}
